// GAVION call-brief-pipeline (spec A3–A4, rulings 9–11): what the card « Avant l'appel » shows. Lines 1–2 (the AI brief)
// from the stored row and the request's answer; line 3 « Historique » from the lead's history (v_timeline rows, the same
// in real and demo mode) and its requested callback. Pure: no React.
import type { TimelineItem } from "../../domain/timeline";
import type {
  CallBriefRequestOutcome,
  CallBriefRequestResult,
  CallBriefRow,
} from "../api/types";

export const BRIEF_POLL_MS = 4_000;
export const BRIEF_WAIT_MS = 30_000;
export const HISTORY_NOTE_MAX = 120;
export const PREFETCH_NEXT = 3;

/** null: not asked yet on this screen; "pending": asked, no answer yet. */
export type BriefRequestState = "pending" | CallBriefRequestOutcome;
export type BriefLines =
  | { kind: "hidden" }
  | { kind: "preparing" }
  | { kind: "unavailable" }
  | { kind: "ok"; whatTheyDo: string; hook: string };

export function briefLines(i: {
  enabled: boolean;
  request: BriefRequestState | null;
  row: CallBriefRow | null;
  timedOut: boolean;
}): BriefLines {
  if (!i.enabled || i.request === "disabled" || i.request === "not_configured")
    return { kind: "hidden" };
  if (i.row?.status === "ok" && i.row.whatTheyDo && i.row.hook)
    return { kind: "ok", whatTheyDo: i.row.whatTheyDo, hook: i.row.hook };
  if (i.row?.status === "failed") return { kind: "unavailable" };
  if (i.request === "cap" || i.request === "error" || i.timedOut)
    return { kind: "unavailable" };
  return { kind: "preparing" };
}

export function requestStateFor(
  id: number,
  r: CallBriefRequestResult,
): BriefRequestState {
  return r.capped.includes(id) ? "cap" : r.outcome;
}

/** Ruling 10: the current lead, then the next 3 queue ids other than it. */
export function prefetchIds(
  current: number | null,
  queue: readonly number[],
): number[] {
  if (current === null) return [];
  const next = [...new Set(queue.filter((id) => id !== current))];
  return [current, ...next.slice(0, PREFETCH_NEXT)];
}

export type HistoryFacts = {
  firstCall: boolean;
  attempt: number;
  last: { outcome: string; at: string } | null;
  note: string | null;
  callbackAt: string | null;
};
const UNREACHED = new Set(["skipped", "unlogged"]);
const newestFirst = (a: TimelineItem, b: TimelineItem) =>
  Date.parse(b.at) - Date.parse(a.at);

/** Ruling 11. */
export function historyFacts(
  items: readonly TimelineItem[],
  nextCallbackAt: string | null,
  now: Date,
): HistoryFacts {
  const reached = items
    .filter(
      (i) =>
        i.kind === "call" &&
        typeof i.meta.outcome === "string" &&
        !UNREACHED.has(i.meta.outcome),
    )
    .sort(newestFirst);
  const texts = items
    .filter(
      (i) =>
        ((i.kind === "company_note" && i.meta.source !== "automation") ||
          i.kind === "call") &&
        (i.body_snippet ?? "").trim() !== "",
    )
    .sort(newestFirst);
  const raw = texts[0]?.body_snippet?.replace(/\s+/g, " ").trim() ?? null;
  const chars = raw ? Array.from(raw) : [];
  const note =
    raw === null
      ? null
      : chars.length > HISTORY_NOTE_MAX
        ? `${chars.slice(0, HISTORY_NOTE_MAX - 1).join("")}…`
        : raw;
  const callbackAt =
    nextCallbackAt && Date.parse(nextCallbackAt) > now.getTime()
      ? nextCallbackAt
      : null;
  return {
    firstCall: reached.length === 0,
    attempt: reached.filter((i) => i.meta.direction === "outbound").length + 1,
    last: reached[0]
      ? { outcome: String(reached[0].meta.outcome), at: reached[0].at }
      : null,
    note,
    callbackAt,
  };
}

type Translate = (key: string, options?: Record<string, unknown>) => string;
const fmt = (locale: "fr" | "en", withTime: boolean) =>
  new Intl.DateTimeFormat(locale === "en" ? "en-CA" : "fr-CA", {
    timeZone: "America/Toronto",
    day: "numeric",
    month: "short",
    ...(withTime ? { hour: "numeric", minute: "2-digit" } : {}),
  });

/** « Appel n° 3 · Message laissé, 14 oct. · Note : « … » · Rappel demandé : 23 oct., 10 h 00 »; « Premier appel ». */
export function historyText(
  f: HistoryFacts,
  t: Translate,
  locale: "fr" | "en",
): string {
  if (f.firstCall) return t("gavion.call_brief.history.first_call");
  const parts = [t("gavion.call_brief.history.attempt", { n: f.attempt })];
  if (f.last)
    parts.push(
      t("gavion.call_brief.history.last", {
        outcome: t(`gavion.call_outcome.${f.last.outcome}`),
        date: fmt(locale, false).format(new Date(f.last.at)),
      }),
    );
  if (f.note) parts.push(t("gavion.call_brief.history.note", { text: f.note }));
  if (f.callbackAt)
    parts.push(
      t("gavion.call_brief.history.callback", {
        date: fmt(locale, true).format(new Date(f.callbackAt)),
      }),
    );
  return parts.join(" · ");
}

/** The `brief` of a v_timeline call row ({what_they_do, hook, generated_at}); anything else is null. */
export function briefOfMeta(
  v: unknown,
): { whatTheyDo: string; hook: string } | null {
  if (typeof v !== "object" || v === null) return null;
  const o = v as Record<string, unknown>;
  return typeof o.what_they_do === "string" &&
    o.what_they_do.trim() &&
    typeof o.hook === "string" &&
    o.hook.trim()
    ? { whatTheyDo: o.what_they_do, hook: o.hook }
    : null;
}

/** Demo twin of private.calls_brief_snapshot (84_): what a call keeps of the lead's ok brief. */
export function briefSnapshot(
  row: Record<string, unknown> | null | undefined,
): { what_they_do: string; hook: string; generated_at: string } | null {
  if (
    !row ||
    row.status !== "ok" ||
    typeof row.what_they_do !== "string" ||
    typeof row.hook !== "string"
  )
    return null;
  return {
    what_they_do: row.what_they_do,
    hook: row.hook,
    generated_at: String(row.generated_at ?? ""),
  };
}
